import { Component, OnInit, inject, ChangeDetectionStrategy, signal, input, OnChanges, SimpleChanges, SimpleChange } from '@angular/core';
import { Router } from '@angular/router';
import {PersonLogin} from '../person/person-login';
import {PersonLoginService} from '../services/person-login-service';
import {SfrManagementService} from '../services/sfr-management-service';
import {StringUtils} from '../utils/string-utils';
import {SfrEntity} from './sfr-entity';
import {SfrEntityLine} from './sfr-entity-line';
import {FormsModule} from '@angular/forms';
import {MatIcon} from '@angular/material/icon';
import {MatButton} from '@angular/material/button';
import {MatProgressSpinner} from '@angular/material/progress-spinner';


@Component({
    selector: 'sfr-management',
    templateUrl: './sfr-management.component.html',
    styleUrls: ['./sfr-management.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatProgressSpinner, MatButton, MatIcon, FormsModule]
})
export class SfrManagementComponent implements OnChanges, OnInit {
  private readonly personLoginService = inject(PersonLoginService);
  private readonly sfrService = inject(SfrManagementService);
  private readonly router = inject(Router);
  readonly entityId = input.required<string>({ alias: 'entity-id' });
  readonly entityRevision = input.required<string>({ alias: 'entity-revision' });


  private personLogin!: PersonLogin;
  public readonly sfrEntity = signal<SfrEntity>(undefined!);
  public readonly isBusy = signal<boolean>(undefined!);
  public readonly errorMessage = signal<string>(undefined!);
  public sfrAddText!: string;
  public selectedTextEntry!: string;
  public filteredLines!: SfrEntityLine[];
  public lineFilter!: string;
  readonly noChanges = signal(true);
  private readonly originalEntity = signal<SfrEntity>(undefined!);

  private initialize(): void {
    this.initializeFromRouteParams();
  }

  private handleError(error: any): void {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

  public backToEntity(): void {
    let pageName = this.sfrEntity().entityKey.entityName === 'QUOTE' ? "quotes" : "orders";
    this.router.navigate([pageName, this.sfrEntity().entityKey.entityId, this.sfrEntity().entityKey.entityRevision]);
  }

  public save(): void {
    this.resetStatus();
    this.sfrService.save(this.sfrEntity()).subscribe(() => {
      this.isBusy.set(false);
      this.noChanges.set(true);
      this.originalEntity.set(JSON.parse(JSON.stringify(this.sfrEntity())));
    }, error => this.handleError(error));
  }

  private resetStatus(): void {
    this.isBusy.set(true);
    this.errorMessage.set(null!);
  }

  private backToMain(): void {
    this.router.navigate(['main']);
  }

  ngOnInit(): void {
    if (this.personLoginService.isOutsideEngineer()) {
      this.router.navigate(['ratings']);
    } else {
      this.isBusy.set(true);
      this.personLogin = this.personLoginService.getCachedLogin()!;
      this.initialize();
    }
  }

  public getEntityName(): string {
    if (this.sfrEntity().entityKey.entityName === 'QUOTE') {
      return 'Quote';
    } else if (this.sfrEntity().entityKey.entityName === 'CONTRACT' || this.sfrEntity().entityKey.entityName === 'ORDER') {
      return 'Order';
    }
    return '';
  }

  public addTextEntry(): void {
    let textToSelect: string = this.sfrAddText;
    let existingEntry: string = this.findEntryInList(this.sfrAddText, this.sfrEntity().sfrTextEntries);
    if (existingEntry) {
      textToSelect = existingEntry;
    } else {
      this.sfrEntity().sfrTextEntries.push(this.sfrAddText);
    }
    this.selectTextEntry(textToSelect);
    this.noChanges.set(!this.hasInformationChanged());
    this.sfrAddText = null!;
  }

  private hasInformationChanged(): boolean {
    if (this.originalEntity() && this.sfrEntity()) {
      let textChanged = this.isTextDifferent(this.originalEntity().sfrTextEntries, this.sfrEntity().sfrTextEntries);
      if (textChanged) {
        return true;
      }
      const originalLineCount = this.originalEntity().lines && this.originalEntity().lines.length > 0 ? this.originalEntity().lines.length : 0;
      const currentLineCount = this.sfrEntity().lines && this.sfrEntity().lines.length > 0 ? this.sfrEntity().lines.length : 0;
      if (originalLineCount !== currentLineCount) {
        return true;
      } else {
        for (const originalLine of this.originalEntity().lines) {
          const filteredLines = this.sfrEntity().lines.filter(line => line.lineId === originalLine.lineId);
          if (filteredLines.length === 1) {
            const currentLine = filteredLines[0];
            textChanged = this.isTextDifferent(originalLine.sfrTextEntries, currentLine.sfrTextEntries);
            if (textChanged) {
              return true;
            }
          } else {
            return true;
          }
        }
      }
    }
    return false;
  }

  private isTextDifferent(first: string[], second: string[]): boolean {
    const firstCount = first && first.length > 0 ? first.length : 0;
    const secondCount = second && second.length > 0 ? second.length : 0;
    if (firstCount !== secondCount) {
      return true;
    } else {
      for (const entry of first) {
        if (second.indexOf(entry) < 0) {
          return true;
        }
      }
      for (const entry of second) {
        if (first.indexOf(entry) < 0) {
          return true;
        }
      }
    }
    return false;
  }

  private findEntryInList(search: string, list: string[]): string {
    if (!search || search.length === 0 || !list || list.length === 0) {
      return null!;
    }
    let existingTextEntries = list.filter(entry => entry.replace(/\s/g, '').toLowerCase() === search.replace(/\s/g, '').toLowerCase());
    return existingTextEntries.length === 0 ? null! : existingTextEntries[0];
  }

  public selectTextEntry(entry: string): void {
    this.selectedTextEntry = entry;
    this.filterSfrLines();
  }

  public filterSfrLines(): void {
    this.filteredLines = this.sfrEntity().lines;
    if (this.lineFilter && this.lineFilter.trim().length > 0) {
      this.filteredLines = this.filteredLines.filter(line => this.filterMatches(line.productId) || this.filterMatches(line.description.replace(/\_/g, '')) || this.filterMatches(line.tags));
    }
    for (let line of this.filteredLines) {
      let entry = this.findEntryInList(this.selectedTextEntry, line.sfrTextEntries);
      line.selected = !!(entry && entry.length > 0);
    }
  }

  private filterMatches(property: string) {
    if (!property || property.length === 0) {
      return false;
    } else if (!this.lineFilter || this.lineFilter.trim().length === 0) {
      return true;
    } else {
      this.lineFilter = this.lineFilter.trim();
      return property.toLowerCase().indexOf(this.lineFilter.toLowerCase(), 0) > -1;
    }
  }

  public toggleAllSelections(): void {
    if (this.allItemsSelected()) {
      for (let entry of this.filteredLines) {
        this.updateTextSelection(entry);
      }
    } else {
      let unselected = this.filteredLines.filter(line => !line.selected);
      for (let entry of unselected) {
        this.updateTextSelection(entry);
      }
    }
  }

  public allItemsSelected(): boolean {
    if (this.filteredLines.length === 0) {
      return true;
    } else {
      let unselected = this.filteredLines.filter(line => !line.selected);
      return unselected.length === 0;
    }
  }

  public updateTextSelection(line: SfrEntityLine): void {
    if (!line.sfrTextEntries) {
      line.sfrTextEntries = [];
    }
    let entry = this.findEntryInList(this.selectedTextEntry, line.sfrTextEntries);
    if (entry && entry.length > 0) {
      let entryIndex: number = line.sfrTextEntries.indexOf(entry, 0);
      line.sfrTextEntries.splice(entryIndex, 1);
    } else {
      line.sfrTextEntries.push(this.selectedTextEntry);
    }
    line.selected = !entry || entry.length === 0;
    this.noChanges.set(!this.hasInformationChanged());
  }


  private initializeFromRouteParams(): void {
    let idString: string = this.entityId();
    let revisionString: string = this.entityRevision();
    if (idString && idString.length > 0 && !isNaN(+revisionString)) {
      this.sfrService.getSfrEntity(idString, +revisionString).subscribe(result => {
        this.originalEntity.set(JSON.parse(JSON.stringify(result)));
        this.sfrEntity.set(result);
        this.isBusy.set(false);
      }, error => this.handleError(error));
    } else {
      this.backToMain();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (Object.values(changes).some((change: SimpleChange) => !change.firstChange)) {
      this.initializeFromRouteParams();
    }
  }
}
